import { Box, FormHelperText, Stack, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import type { ComponentType, FC } from 'react';

import FormField from 'components/common/formikForm/formField';

import AuFlag from 'assets/flags/AuFlag';
import CaFlag from 'assets/flags/CaFlag';
import UkFlag from 'assets/flags/UkFlag';
import useTranslate from 'hooks/useTranslate';
import type {
    ConsultantLicense,
    ConsultantLicenseFormValues,
    MyLicensesFormValues,
    ServiceConsultantCountryCode,
} from 'types';

interface CountryFlagProps {
    height?: string;
    width?: string;
}

interface LicenseCountryOption {
    Flag: ComponentType<CountryFlagProps>;
    labelKey: string;
    value: ServiceConsultantCountryCode;
}

export const LICENSE_COUNTRY_OPTIONS: LicenseCountryOption[] = [
    { Flag: CaFlag, labelKey: 'canada', value: 'CA' },
    { Flag: AuFlag, labelKey: 'australia', value: 'AU' },
    { Flag: UkFlag, labelKey: 'unitedKingdom', value: 'UK' },
];

export const toCountryLicenseFormValues = (
    licenses: ConsultantLicense[]
): ConsultantLicenseFormValues[] =>
    LICENSE_COUNTRY_OPTIONS.map(({ value }) => {
        const license = licenses.find((currentLicense) => currentLicense.country === value);
        return {
            id: license?.id,
            country: value,
            date_issued: license?.date_issued ?? '',
            license_number: license?.license_number ?? '',
        };
    });

export const hasLicenseDetails = (license: ConsultantLicenseFormValues): boolean =>
    Boolean(license.license_number.trim() || license.date_issued);

export const getPopulatedLicenseFormValues = (
    licenses: ConsultantLicenseFormValues[]
): ConsultantLicenseFormValues[] => licenses.filter(hasLicenseDetails);

export const CountryLicenseFields: FC = () => {
    const { translate } = useTranslate();
    const { errors, isSubmitting, submitCount, touched, values } =
        useFormikContext<MyLicensesFormValues>();
    const licensesError =
        typeof errors.licenses === 'string' && (Boolean(touched.licenses) || submitCount > 0)
            ? errors.licenses
            : null;

    return (
        <Stack spacing={1} sx={{ pt: 4 }}>
            {LICENSE_COUNTRY_OPTIONS.map(({ Flag, labelKey, value }) => {
                const index = values.licenses.findIndex((license) => license.country === value);
                if (index === -1) return null;

                const fieldPrefix = `licenses.${index}`;
                return (
                    <Box key={value} sx={{ py: 0.25 }}>
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 1.5,
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'minmax(120px, 0.8fr) minmax(0, 1fr) minmax(190px, 1.2fr)',
                                },
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                    alignItems: 'center',
                                    alignSelf: 'flex-start',
                                    minHeight: 40,
                                }}
                            >
                                <Flag width="32" height="24" />
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {translate(labelKey)}
                                </Typography>
                            </Stack>

                            <FormField
                                name={`${fieldPrefix}.license_number`}
                                label="licenseNumber"
                                disabled={isSubmitting}
                            />

                            <Box
                                sx={{
                                    '& .MuiPickersInputBase-sectionsContainer': {
                                        width: 'auto',
                                        minWidth: 0,
                                        maxWidth: '100%',
                                    },
                                }}
                            >
                                <FormField
                                    maxDaysFromToday
                                    type="date"
                                    name={`${fieldPrefix}.date_issued`}
                                    label="dateIssued"
                                    disabled={isSubmitting}
                                />
                            </Box>
                        </Box>
                    </Box>
                );
            })}

            {licensesError ? <FormHelperText error>{licensesError}</FormHelperText> : null}
        </Stack>
    );
};
